import { type Theme, Typography } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import type { AnimeatableIconRef } from '@parkdna/ui/icons/types';
import { useTheme } from '@parkdna/ui/theme';
import type { LucideIcon } from 'lucide-react';
import type { ForwardRefExoticComponent, RefAttributes } from 'react';
import { useCallback, useRef, useState } from 'react';
import { SubMenu } from 'react-pro-sidebar';

import { useSidebarStore } from '@/stores/sidebar';

import MenuIcon, { type MenuIconRef } from './MenuIcon';

type AnimatedIconComponent = ForwardRefExoticComponent<
  { size?: number } & RefAttributes<AnimeatableIconRef>
>;

type IconComponent = AnimatedIconComponent | LucideIcon;

type SidebarSubMenuProps = {
  iconComponent: IconComponent;
  dataCy?: string;
  label: string;
  children?: React.ReactNode;
};

export default function SidebarSubMenu({
  iconComponent: Icon,
  dataCy,
  label,
  children,
}: SidebarSubMenuProps) {
  const isSidebarCollapsed = useSidebarStore((state) => state.isCollapsed);
  const [isOpen, setIsOpen] = useState(false);
  const [theme] = useTheme();
  const isOverlay = useMediaQuery((themeMUI: Theme) =>
    themeMUI.breakpoints.down('md')
  );
  const menuIconRef = useRef<MenuIconRef>(null);

  const onOpenChange = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  const onSubMenuClick = useCallback(() => {
    if (!isSidebarCollapsed) return;

    setIsOpen((prev) => !prev);
  }, [isSidebarCollapsed]);

  const handleMouseEnter = useCallback(() => {
    menuIconRef.current?.startAnimation();
  }, []);

  const handleMouseLeave = useCallback(() => {
    menuIconRef.current?.stopAnimation();
  }, []);

  const tooltipTitle = `${label} \u2192`;
  const shouldShowTooltip = isSidebarCollapsed && !isOverlay;

  return (
    <SubMenu
      onClick={onSubMenuClick}
      style={{
        fontSize: theme.typography.overline.fontSize,
        display: 'flex',
        alignItems: 'center',
      }}
      open={isOpen}
      onOpenChange={onOpenChange}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      label={
        <Typography
          variant="subtitle1"
          component="span"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            verticalAlign: 'middle',
            lineHeight: 1,
          }}
        >
          {label}
        </Typography>
      }
      icon={
        <MenuIcon
          ref={menuIconRef}
          Icon={Icon}
          title={tooltipTitle}
          shouldShowTooltip={shouldShowTooltip}
          size={20}
        />
      }
      data-cy={dataCy}
      title={isSidebarCollapsed ? label : undefined}
      aria-label={isSidebarCollapsed ? label : undefined}
    >
      {children}
    </SubMenu>
  );
}
